<script setup>
defineProps({
	primaryStockSymbol: {
		type: String,
		default: null
	},
	primaryStockData: {
		type: Object,
		default: null
	}
});

const WEEKEND_WINDFALLS_URL =
	"https://trading.timothysykes.com/weekend-windfalls/step/weekend-windfalls-v3/?utm_medium=ts.com&utm_campaign=Pl3JLJ";

const ctaUrl = useCampaignUrl(WEEKEND_WINDFALLS_URL);
</script>

<template>
	<section
		v-if="primaryStockSymbol"
		class="market-data-bar"
	>
		<!-- Icon -->
		<div class="market-data-bar__icon-circle">
			<svg
				width="18"
				height="18"
				viewBox="0 0 24 24"
				fill="none"
			>
				<path
					d="M4 4v16h16"
					stroke="currentColor"
					stroke-width="2.5"
					stroke-linecap="round"
					stroke-linejoin="round"
				/>
				<path
					d="M7 16l4-5 3 3 6-8"
					stroke="currentColor"
					stroke-width="2.5"
					stroke-linecap="round"
					stroke-linejoin="round"
				/>
			</svg>
		</div>

		<div class="market-data-bar__inline">
			<a
				:href="ctaUrl"
				target="_blank"
				rel="noopener noreferrer"
				class="market-data-bar__link"
			>
				Is it time to buy
				<b>
					{{ primaryStockSymbol.replace(/\s+/g, "") }}
					<span class="font-normal">?</span>
				</b>
			</a>
			<span
				v-if="primaryStockData"
				class="inline-flex lg:!hidden gap-1 market-data-bar__inline-change"
			>
				It's {{ primaryStockData.PC_CHG > 0 ? "up" : "down" }}
				<strong :class="primaryStockData.PC_CHG > 0 ? 'text-up' : 'text-down'">
					{{ primaryStockData.PC_CHG > 0 ? "+" : ""
					}}{{ primaryStockData.PC_CHG?.toFixed(2) }}%
				</strong>
				today
			</span>
		</div>

		<div class="market-data-bar__desktop-right">
			<span class="market-data-bar__label">MARKET DATA</span>
			<NuxtLink
				class="market-data-bar__ticker hover:underline"
				:to="`/quote/${primaryStockSymbol}`"
			>
				<span class="market-data-bar__symbol">{{ primaryStockSymbol }}</span>
				<span
					v-if="primaryStockData"
					:class="[
						'market-data-bar__change',
						primaryStockData.PC_CHG > 0 ? 'up' : 'down'
					]"
				>
					{{ primaryStockData.PC_CHG > 0 ? "+" : ""
					}}{{ primaryStockData.PC_CHG?.toFixed(2) }}%
					{{ primaryStockData.PC_CHG > 0 ? "▲" : "▼" }}
				</span>
			</NuxtLink>
		</div>

		<!-- Mobile chevron -->
		<a
			href="https://trading.timothysykes.com/ts1xgpt/step/ts1xgpt-vsl/?utm_source=GDN&utm_medium=CPC&utm_content=TS&utm_term=gr_tim_sykes&utm_campaign=EUMyOJ"
			target="_blank"
			rel="noopener noreferrer"
			class="market-data-bar__chevron-link"
		>
			<svg
				class="market-data-bar__chevron"
				width="20"
				height="20"
				viewBox="0 0 24 24"
				fill="none"
			>
				<path
					d="M9 18l6-6-6-6"
					stroke="#9ca3af"
					stroke-width="2"
					stroke-linecap="round"
					stroke-linejoin="round"
				/>
			</svg>
		</a>
	</section>
</template>

<style lang="scss">
.market-data-bar {
	display: flex;
	align-items: center;
	padding: 12px 16px;
	background: rgba($primary-300, 0.1);
	border: 1px solid rgba($primary-500, 0.2);
	border-radius: 12px;
	margin-bottom: 24px;
	gap: 12px;

	@media (min-width: 768px) {
		padding: 14px 20px;
		gap: 16px;
	}
}

.market-data-bar__icon-circle {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 24px;
	height: 24px;
	min-width: 24px;
	border-radius: 50%;
	background: #ffffff;
	color: #3b82f6;
	border: 1px solid rgba(#3b82f6, 0.2);

	@media (min-width: 768px) {
		width: 32px;
		height: 32px;
		min-width: 32px;
	}
}

/* Mobile: inline flowing text */
.market-data-bar__inline {
	display: flex;
	flex-wrap: wrap;
	gap: 0 4px;
	flex: 1;
	font-size: 14px;
	font-weight: 500;
	color: #374151;
	line-height: 1.5;
}

.market-data-bar__link {
	text-decoration: none;
	color: inherit;

	&:hover {
		text-decoration: underline;
	}
}

.market-data-bar__inline-change {
	.text-up {
		color: #16a34a;
	}

	.text-down {
		color: #dc2626;
	}
}

/* Desktop: split right section */
.market-data-bar__desktop-right {
	display: none;

	@media (min-width: 768px) {
		display: flex;
		flex-direction: column;
		align-items: flex-end;
		gap: 2px;
		margin-left: auto;
	}
}

.market-data-bar__label {
	font-size: 10px;
	font-weight: 600;
	color: #9ca3af;
	letter-spacing: 0.08em;
	text-transform: uppercase;
}

.market-data-bar__ticker {
	display: flex;
	align-items: center;
	gap: 8px;
}

.market-data-bar__symbol {
	font-size: 16px;
	font-weight: 700;
	color: #111827;
}

.market-data-bar__change {
	font-size: 14px;
	font-weight: 600;
	padding: 1px 4px;
	border-radius: 4px;

	&.up {
		color: #16a34a;
		background-color: rgba(#16a34a, 0.1);
	}

	&.down {
		color: #dc2626;
		background-color: rgba(#dc2626, 0.1);
	}
}

/* Mobile chevron */
.market-data-bar__chevron-link {
	flex-shrink: 0;
	display: flex;

	@media (min-width: 768px) {
		display: none;
	}
}

.market-data-bar__chevron {
	flex-shrink: 0;
}
</style>
